import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { motion } from "motion/react";
import { MODULES } from "@kipo/shared";
import { KipoMark } from "../../ui/Icon";
import { Button, ErrorBox } from "../../ui/kit";
import { auth } from "../../lib/session";
import { queryClient } from "../../lib/trpc";

const BLOCKS = [
  { m: MODULES.projects, line: "Kanban, Gantt, sprint e ore vendute" },
  { m: MODULES.crm, line: "Pipeline, aziende, contatti e attività" },
  { m: MODULES.sales, line: "Preventivi, fatture, scadenzario e SDI" },
  { m: MODULES.marketing, line: "Campagne, form e automazioni" },
];

/** Layout delle pagine di accesso: i quattro blocchi colorati dei moduli. */
function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-surface flex flex-wrap text-ink">
      <div className="flex-[1_1_560px] min-w-0 p-6 sm:p-10 flex flex-col gap-6 bg-stone">
        <div className="flex items-center gap-3">
          <KipoMark size={28} gap={3} radius={3} />
          <span className="font-bold text-[22px] tracking-[-0.03em]">kipo</span>
        </div>
        <div className="flex-1 grid grid-cols-2 grid-rows-2 gap-3.5 min-h-[420px] lg:min-h-[560px]">
          {BLOCKS.map(({ m, line }, i) => (
            <motion.div
              key={m.key}
              className="rounded-[22px] p-6 sm:p-7 flex flex-col justify-between text-white"
              style={{ background: m.color }}
              initial={{ opacity: 0, y: 18, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.08 * i, type: "spring", stiffness: 260, damping: 26 }}
            >
              <div className="text-[15px] opacity-85">{m.name}</div>
              <div className="text-[22px] sm:text-[30px] font-semibold tracking-[-0.03em] leading-[1.05] max-w-[14ch]">{line}</div>
            </motion.div>
          ))}
        </div>
        <div className="text-sm text-muted">Quattro moduli, un solo account, un solo abbonamento.</div>
      </div>
      <div className="flex-[1_1_440px] min-w-0 flex items-center justify-center px-6 sm:px-10 py-14">{children}</div>
    </div>
  );
}

const big = "h-[52px] w-full rounded-[14px] border-2 px-4 text-base bg-surface text-ink placeholder:text-subtle outline-none transition-colors border-line-strong focus:border-ink";

function BigInput({ label, id, ...rest }: React.InputHTMLAttributes<HTMLInputElement> & { label: string; id: string }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <input id={id} {...rest} className={big} />
    </div>
  );
}

function useSubmit(fn: () => Promise<void>) {
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  return { submit, error, loading };
}

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { submit, error, loading } = useSubmit(async () => {
    await auth.login(email, password);
    queryClient.clear();
    navigate({ to: "/" });
  });
  return (
    <AuthLayout>
      <form onSubmit={submit} className="w-full max-w-[400px] flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-[40px] font-semibold tracking-[-0.04em] leading-none">Entra in Kipo</h1>
          <p className="m-0 text-[15px] text-muted">
            Oppure{" "}
            <Link to="/register" className="font-medium text-ink underline underline-offset-4 decoration-line-strong hover:decoration-ink">
              crea l'account della tua azienda
            </Link>
            , è gratis per 7 giorni.
          </p>
        </div>
        <BigInput id="email" label="Email di lavoro" type="email" autoComplete="email" required placeholder="nome@azienda.it" value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="flex flex-col gap-2">
          <BigInput id="password" label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          <Link to="/forgot-password" className="text-[13px] text-muted self-end hover:text-ink">
            Ho dimenticato la password
          </Link>
        </div>
        <ErrorBox error={error} />
        <Button type="submit" variant="primary" size="lg" loading={loading}>
          Accedi
        </Button>
        <p className="m-0 text-xs text-subtle leading-relaxed">Accedendo accetti le condizioni d'uso. I dati restano in Unione Europea.</p>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [f, setF] = useState({ companyName: "", firstName: "", lastName: "", email: "", password: "" });
  const [accept, setAccept] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const { submit, error, loading } = useSubmit(async () => {
    if (!accept) throw new Error("Accetta le condizioni d'uso per continuare");
    await auth.register({ ...f, acceptTerms: true });
    queryClient.clear();
    navigate({ to: "/" });
  });
  return (
    <AuthLayout>
      <form onSubmit={submit} className="w-full max-w-[420px] flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-[36px] font-semibold tracking-[-0.04em] leading-none">Prova Kipo per 7 giorni</h1>
          <p className="m-0 text-[15px] text-muted">Tutti i moduli, senza carta. Poi 14 € al mese per utente.</p>
        </div>
        <BigInput id="company" label="Nome dell'azienda" required value={f.companyName} onChange={set("companyName")} autoComplete="organization" />
        <div className="grid grid-cols-2 gap-3">
          <BigInput id="first" label="Nome" required value={f.firstName} onChange={set("firstName")} autoComplete="given-name" />
          <BigInput id="last" label="Cognome" required value={f.lastName} onChange={set("lastName")} autoComplete="family-name" />
        </div>
        <BigInput id="email" label="Email di lavoro" type="email" required value={f.email} onChange={set("email")} autoComplete="email" />
        <BigInput id="password" label="Password (almeno 10 caratteri)" type="password" minLength={10} required value={f.password} onChange={set("password")} autoComplete="new-password" />
        <label className="flex gap-2.5 text-sm text-text2 items-start">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1 h-4 w-4 accent-ink" />
          Accetto le condizioni d'uso e l'informativa privacy.
        </label>
        <ErrorBox error={error} />
        <Button type="submit" variant="primary" size="lg" loading={loading}>
          Crea lo spazio di lavoro
        </Button>
        <p className="m-0 text-sm text-muted">
          Hai già un account?{" "}
          <Link to="/login" className="font-medium text-ink underline underline-offset-4">
            Accedi
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { submit, error, loading } = useSubmit(async () => {
    await auth.forgot(email);
    setSent(true);
  });
  return (
    <AuthLayout>
      <form onSubmit={submit} className="w-full max-w-[400px] flex flex-col gap-6">
        <h1 className="m-0 text-[36px] font-semibold tracking-[-0.04em] leading-none">Reimposta la password</h1>
        {sent ? (
          <p className="m-0 text-[15px] text-text2">Se l'indirizzo è registrato riceverai un'email con il link. Il link vale un'ora.</p>
        ) : (
          <>
            <BigInput id="email" label="Email di lavoro" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <ErrorBox error={error} />
            <Button type="submit" variant="primary" size="lg" loading={loading}>
              Invia il link
            </Button>
          </>
        )}
        <Link to="/login" className="text-sm text-muted hover:text-ink">
          Torna all'accesso
        </Link>
      </form>
    </AuthLayout>
  );
}

export function ResetPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const { submit, error, loading } = useSubmit(async () => {
    await auth.reset(token ?? "", password);
    navigate({ to: "/login" });
  });
  return (
    <AuthLayout>
      <form onSubmit={submit} className="w-full max-w-[400px] flex flex-col gap-6">
        <h1 className="m-0 text-[36px] font-semibold tracking-[-0.04em] leading-none">Scegli una nuova password</h1>
        <BigInput id="password" label="Nuova password (almeno 10 caratteri)" type="password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} />
        <ErrorBox error={error} />
        <Button type="submit" variant="primary" size="lg" loading={loading}>
          Salva la password
        </Button>
      </form>
    </AuthLayout>
  );
}

export function AcceptInvitePage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const navigate = useNavigate();
  const [f, setF] = useState({ firstName: "", lastName: "", password: "" });
  const { submit, error, loading } = useSubmit(async () => {
    await auth.acceptInvite({ token: token ?? "", ...(f.password ? f : {}) });
    queryClient.clear();
    navigate({ to: "/" });
  });
  return (
    <AuthLayout>
      <form onSubmit={submit} className="w-full max-w-[420px] flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-[36px] font-semibold tracking-[-0.04em] leading-none">Unisciti al team</h1>
          <p className="m-0 text-[15px] text-muted">Se hai già un account Kipo con questa email lascia vuoti i campi e conferma.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <BigInput id="first" label="Nome" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} />
          <BigInput id="last" label="Cognome" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} />
        </div>
        <BigInput id="password" label="Password" type="password" minLength={10} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <ErrorBox error={error} />
        <Button type="submit" variant="primary" size="lg" loading={loading}>
          Accetta l'invito
        </Button>
      </form>
    </AuthLayout>
  );
}
